"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import Link from "next/link";
import ChevronLeftIcon from "../icons/ChevronLeftIcon";
import ChevronRightIcon from "../icons/ChevronRightIcon";
import ChevronDownIcon from "../icons/ChevronDownIcon";
import ChevronUpIcon from "../icons/ChevronUpIcon";
import FlameFilledIcon from "../icons/FlameFilledIcon";
import Button from "../ui/Button";
// Social media icons - keeping react-icons for now as we don't have custom versions
import { SlSocialYoutube } from "react-icons/sl";
import { SlSocialFacebook } from "react-icons/sl";
import { SlSocialInstagram } from "react-icons/sl";
import { SlSocialTwitter } from "react-icons/sl";
import Image from "next/image";
import {
  API_BASE_URL,
  DEFAULT_COLUMNS_COUNT,
  FOOTER_COLUMN_CLASS,
  SOCIAL_LINKS,
} from "@/lib/constants";
import { BaseProps } from "@/lib/types/common";
import { getCurrentYear } from "@/lib/utils/common";
import { useScrollPosition } from "@/lib/hooks/useScrollPosition";
import { useNewsletter } from "@/lib/hooks/useNewsletter";

/**
 * Footer social row. Renders only the networks Dank actually has an account on,
 * so a missing key means no dead icon rather than a link to a network homepage.
 */
const SOCIAL_NETWORKS = [
  { key: "instagram", label: "Instagram", Icon: SlSocialInstagram },
  { key: "facebook", label: "Facebook", Icon: SlSocialFacebook },
  { key: "youtube", label: "YouTube", Icon: SlSocialYoutube },
  { key: "twitter", label: "X", Icon: SlSocialTwitter },
] as const;

function SocialLinks({
  iconClassName,
  linkClassName = "text-blue-100 transition-colors",
}: {
  iconClassName: string;
  linkClassName?: string;
}) {
  return (
    <>
      {SOCIAL_NETWORKS.map(({ key, label, Icon }) => {
        const href = SOCIAL_LINKS[key];
        if (!href) return null;
        return (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClassName}
            aria-label={label}
          >
            <Icon className={iconClassName} />
          </a>
        );
      })}
    </>
  );
}
type MenuItem = {
  id: string;
  label: string;
  type: "collection" | "link" | "section" | "group" | "delivery-info";
  sortOrder?: number;
  url?: string;
  collectionId?: string;
  collection?: {
    id: string;
    title: string;
    handle: string;
    bannerImageUrl?: string;
    description?: string;
  };
  children?: MenuItem[];
  column?: number;
  badge?: string;
  badgeColor?: string;
  description?: string;
};

type Menu = {
  id: string;
  name: string;
  position: string | null;
  items: MenuItem[];
};

type FooterProps = BaseProps;

export default function Footer({ base }: FooterProps = {}) {
  const currentYear = new Date().getFullYear();
  const newsletter = useNewsletter(base);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [canScroll, setCanScroll] = useState(false);
  const [hasMeasuredScroll, setHasMeasuredScroll] = useState(false);
  const [columnsCount, setColumnsCount] = useState(6); // fallback
  const [footerMenu, setFooterMenu] = useState<Menu | null>(null);
  const [megaFooterMenu, setMegaFooterMenu] = useState<Menu | null>(null);
  const [expandedDeliveryInfo, setExpandedDeliveryInfo] = useState<
    string | null
  >(null);
  const [expandedMobileSection, setExpandedMobileSection] = useState<string | null>(null);
  const [expandedMobileDeliveryLocation, setExpandedMobileDeliveryLocation] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Fetch mega footer navigation menu (above divider)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${base || API_BASE_URL}/api/navigation/menu/mega-footer`,
          {
            cache: "no-store",
          }
        );
        if (res.ok) {
          const data = await res.json();
          if (data?.success && data.menu) {
            setMegaFooterMenu(data.menu);
          }
        }
      } catch (err) {
        // Silently handle network errors
        if (process.env.NODE_ENV === "development") {
          console.warn("Failed to load mega footer navigation menu:", err);
        }
      }
    })();
  }, [base]);

  // Fetch footer navigation menu (below divider)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${base || API_BASE_URL}/api/navigation/menu/footer`,
          {
            cache: "no-store",
          }
        );
        if (res.ok) {
          const data = await res.json();
          if (data?.success && data.menu) {
            setFooterMenu(data.menu);
          }
        }
      } catch (err) {
        // Silently handle network errors
        if (process.env.NODE_ENV === "development") {
          console.warn("Failed to load footer navigation menu:", err);
        }
      }
    })();
  }, [base]);

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setIsAtStart(scrollLeft <= 0);
    setIsAtEnd(scrollLeft >= scrollWidth - clientWidth - 1); // -1 for rounding errors
    setCanScroll(scrollWidth > clientWidth + 1);
    setHasMeasuredScroll(true);
  }, []);

  useEffect(() => {
    const columns = scrollContainerRef.current?.querySelectorAll(
      `.${FOOTER_COLUMN_CLASS}`
    );
    if (columns && columns.length) {
      setColumnsCount(columns.length);
    }
  }, [footerMenu]);

  // Update scroll button visibility based on scroll position
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Initial check deferred to next frame so the mount-time layout read
    // batches with the browser's first paint instead of forcing reflow.
    const raf = requestAnimationFrame(updateScrollState);

    container.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      cancelAnimationFrame(raf);
      container.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  // Re-measure after the footer nav content renders/changes (API load, expand/collapse, store list)
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const raf = requestAnimationFrame(() => updateScrollState());
    return () => cancelAnimationFrame(raf);
  }, [footerMenu, expandedDeliveryInfo, columnsCount, updateScrollState]);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await newsletter.submit(base);
  };

  const handleScrollLeft = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ left: 0, behavior: "smooth" });
  };

  const handleScrollRight = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const maxScroll = container.scrollWidth - container.clientWidth;
    container.scrollTo({ left: maxScroll, behavior: "smooth" });
  };

  const getMenuItemUrl = (item: MenuItem): string => {
    if (item.type === "collection" && item.collection) {
      return `/collections/${item.collection.handle}`;
    }
    return item.url || "#";
  };

  return (
    <footer className="w-full bg-constant-white border-t border-neutral-10 mt-auto rounded-t-3xl">
      <div className="w-full px-4 pt-3 sm:py-6 sm:px-6 lg:px-10">
        {/* Mobile Layout */}
        <div className="lg:hidden">
          {/* Top Section: Logo and Social Icons */}
          <div className="flex items-center justify-between mb-4 gap-3">
            <Link href="/">
              <Image
                src="/media/brand/footer-logo.webp"
                alt="Dank Cannabis"
                width={109}
                height={56}
                className="h-[56px] w-auto"
              />
            </Link>
            <div className="flex items-center gap-6">
              <SocialLinks iconClassName="w-6 h-6" />
            </div>
          </div>

          {/* Primary Navigation Links */}
          {megaFooterMenu && megaFooterMenu.items && megaFooterMenu.items.length > 0 && (
            <>
              <div className="w-full h-px bg-neutral-10 mb-3" />
              <div className="flex items-center justify-between gap-4 mb-4">
                {megaFooterMenu.items
                  .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                  .map((item) => (
                    <Link
                      key={item.id}
                      href={getMenuItemUrl(item)}
                      className="text-sm font-medium text-text-secondary transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
              </div>
            </>
          )}

          {/* Collapsible Sections */}
          {footerMenu && footerMenu.items && footerMenu.items.length > 0 && (
            <div className="space-y-0">
              {footerMenu.items
                .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                .map((group) => {
                  const hasChildren = group.children && group.children.length > 0;
                  const isExpanded = expandedMobileSection === group.id;

                  // Handle delivery-info type
                  if (group.type === "delivery-info") {
                    let deliveryData: any = null;
                    try {
                      if (group.description) {
                        deliveryData = JSON.parse(group.description);
                      }
                    } catch (e) {
                      console.warn("Failed to parse delivery info:", e);
                    }

                    if (!deliveryData) return null;
                    const configuredLocations = (group.children || [])
                      .slice()
                      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

                    return (
                      <div key={group.id} className="border-t border-neutral-10">
                        {/* TODO(button-migration): accordion with text + chevron-in-circle right side. */}
                        <Button raw
                          type="button"
                          onClick={() =>
                            setExpandedMobileSection(isExpanded ? null : group.id)
                          }
                          className="flex w-full items-center justify-between px-1 py-4 text-base font-semibold text-text-primary transition-colors"
                        >
                          <span>{group.label}</span>
                          <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-neutral-10">
                            {isExpanded
                              ? <ChevronUpIcon size={14} className="text-text-secondary" />
                              : <ChevronDownIcon size={14} className="text-text-secondary" />
                            }
                          </div>
                        </Button>
                        {isExpanded && (
                          <div className="pb-4 pl-1 space-y-2">
                            {deliveryData.showBadge && (
                              <div className="inline-flex items-center justify-center gap-1.5 px-2 py-1 bg-yellow-50 rounded-[6px] text-xs font-medium text-text-primary">
                                <FlameFilledIcon size={12} className="w-3 h-3" />
                                {deliveryData.badgeText}
                              </div>
                            )}
                            <div className="text-sm text-text-secondary">
                              Open: {deliveryData.hours}
                            </div>
                            <div className="text-sm text-text-secondary">
                              Min order: ${deliveryData.minOrder}
                            </div>
                            <div className="text-sm text-text-secondary">
                              Delivery fee: ${deliveryData.deliveryFee.toFixed(2)}
                            </div>
                            <div className="text-sm text-text-secondary">
                              Free delivery: ${deliveryData.freeDeliveryThreshold}+
                            </div>
                            {configuredLocations.length > 0 && (
                              <div className="pt-1">
                                {/* TODO(button-migration): accordion with text + chevron in circle. */}
                                <Button raw
                                  type="button"
                                  onClick={() =>
                                    setExpandedMobileDeliveryLocation(
                                      expandedMobileDeliveryLocation === group.id
                                        ? null
                                        : group.id
                                    )
                                  }
                                  className="flex items-center gap-2 text-sm font-medium text-text-secondary transition-colors"
                                >
                                  <span>Select location</span>
                                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-neutral-10">
                                    <ChevronDownIcon
                                      size={12}
                                      className={`transition-transform text-text-secondary ${
                                        expandedMobileDeliveryLocation === group.id ? "rotate-180" : ""
                                      }`}
                                    />
                                  </div>
                                </Button>
                                {expandedMobileDeliveryLocation === group.id && (
                                  <div className="mt-2 space-y-1 pl-1">
                                    {configuredLocations.map((item) => (
                                      <Link
                                        key={item.id}
                                        href={getMenuItemUrl(item)}
                                        className="block py-1.5 text-sm text-text-secondary transition-colors"
                                      >
                                        {item.type === "collection" && item.collection
                                          ? item.collection.title
                                          : item.label}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Handle regular group items
                  return (
                    <div key={group.id} className="border-t border-neutral-10">
                      {hasChildren ? (
                        // TODO(button-migration): accordion with text + chevron-in-circle.
                        <Button raw
                          type="button"
                          onClick={() =>
                            setExpandedMobileSection(isExpanded ? null : group.id)
                          }
                          className="flex w-full items-center justify-between px-1 py-4 text-base font-semibold text-text-primary transition-colors"
                        >
                          <span>{group.label}</span>
                          <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-neutral-10">
                            {isExpanded
                              ? <ChevronUpIcon size={14} className="text-text-secondary" />
                              : <ChevronDownIcon size={14} className="text-text-secondary" />
                            }
                          </div>
                        </Button>
                      ) : (
                        <div className="px-1 py-4 text-base font-semibold text-text-primary">
                          {group.label}
                        </div>
                      )}
                      {hasChildren && isExpanded && (
                        <div className="pb-4 pl-1 space-y-0.5">
                          {group.children
                            ?.slice()
                            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                            .map((item) => (
                              <Link
                                key={item.id}
                                href={getMenuItemUrl(item)}
                                className="block rounded-lg px-1 py-2 text-sm text-text-secondary transition-colors"
                              >
                                {item.type === "collection" && item.collection
                                  ? item.collection.title
                                  : item.label}
                              </Link>
                            ))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}

          {/* Email Subscription Section */}
          <div className="border-t border-neutral-10 pt-4 pb-4">
            <h2 className="text-xl font-bold text-text-secondary mb-3">
              Fresh Dank in your inbox
            </h2>
            <form
              onSubmit={handleNewsletterSubmit}
              className="flex items-center gap-2"
            >
              <input
                type="email"
                value={newsletter.email}
                onChange={(e) => newsletter.setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={newsletter.loading}
                className="flex-1 rounded-xl border border-neutral-10 bg-constant-white px-4 py-2.5 text-sm font-medium text-text-primary placeholder:text-text-tertiary focus:border-blue-80 focus:outline-none focus:ring-2 focus:ring-blue-80/20 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <Button
                type="submit"
                variant="brand"
                size="lg"
                disabled={newsletter.loading}
                className="rounded-xl"
                startIcon={
                  newsletter.loading ? (
                    <svg className="h-4 w-4 animate-spin flex-shrink-0" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : undefined
                }
              >
                {newsletter.loading ? "Sending…" : "Subscribe"}
              </Button>
            </form>
            {newsletter.success && (
              <p className="mt-2 text-xs text-green-600">
                Thank you for subscribing!
              </p>
            )}
            {newsletter.error && (
              <p className="mt-2 text-xs text-red-600">{newsletter.error}</p>
            )}
          </div>

          {/* Footer Links */}
          <div className="border-t border-neutral-10 pt-4 pb-4 w-full overflow-x-auto scrollbar-hide">
            <div className="flex w-max gap-3">
              <Link
                href="/privacy-policy"
                className="text-sm font-medium text-text-secondary transition-colors whitespace-nowrap"
              >
                Privacy Policy
              </Link>
              <div className="w-[2px] h-5 bg-neutral-10 flex-shrink-0" />
              <Link
                href="/refund-policy"
                className="text-sm font-medium text-text-secondary transition-colors whitespace-nowrap"
              >
                Refund Policy
              </Link>
              <div className="w-[2px] h-5 bg-neutral-10 flex-shrink-0" />
              <Link
                href="/terms-of-service"
                className="text-sm font-medium text-text-secondary transition-colors whitespace-nowrap"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:block">
          {/* Mega Footer Navigation (Above Divider) */}
          <nav className="flex items-center justify-between w-full mb-6">
          <div className="inline-flex items-center gap-4">
            <Link href="/">
              <Image
                src="/media/brand/footer-logo.webp"
                alt="Dank Cannabis"
                width={109}
                height={56}
                className="h-[56px] w-auto"
              />
            </Link>

            <div className="hidden lg:block w-px h-[34px] bg-neutral-10" />

            {/* Dynamic Mega Footer Navigation from API */}
            <div className="hidden lg:flex items-center gap-6">
              {megaFooterMenu &&
              megaFooterMenu.items &&
              megaFooterMenu.items.length > 0
                ? megaFooterMenu.items
                    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                    .map((item) => {
                      const url =
                        item.type === "collection" && item.collection
                          ? `/collections/${item.collection.handle}`
                          : item.url || "#";

                      return (
                        <Link
                          key={item.id}
                          href={url}
                          className="text-base font-medium text-text-secondary transition-colors whitespace-nowrap"
                        >
                          {item.label}
                        </Link>
                      );
                    })
                : null}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <SocialLinks iconClassName="w-5 h-5" linkClassName="text-text-secondary transition-colors" />
          </div>
        </nav>

        <div className="w-full h-px bg-neutral-10 mb-6" />

        {/* Scrollable Content Container */}
        <div className="relative">
          {/* Left Arrow Button (Previous) */}
          {hasMeasuredScroll && canScroll && !isAtStart && (
            // TODO(button-migration): absolute-positioned scroll nav button.
            <Button raw
              type="button"
              onClick={handleScrollLeft}
              className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-xl border border-blue-100 bg-blue-100 p-2 text-constant-white shadow-lg transition-colors hover:border-neutral-70"
              aria-label="Scroll left"
            >
              <ChevronLeftIcon size={20} color="currentColor" className="h-5 w-5" />
            </Button>
          )}

          {/* Right Arrow Button (Next) */}
          {hasMeasuredScroll && canScroll && !isAtEnd && (
            // TODO(button-migration): absolute-positioned scroll nav button.
            <Button raw
              type="button"
              onClick={handleScrollRight}
              className="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-xl border border-blue-100 bg-blue-100 p-2 text-constant-white shadow-lg transition-colors hover:border-neutral-70"
              aria-label="Scroll right"
            >
              <ChevronRightIcon size={20} color="currentColor" className="h-5 w-5" />
            </Button>
          )}

          {/* Main Footer Content - Scrollable */}
          <div
            ref={scrollContainerRef}
            className="overflow-hidden scrollbar-hide mb-6"
          >
            <div className="flex gap-[100px]">
              {/* Dynamic Navigation Columns from API - Use exactly what's configured in admin */}
              {footerMenu && footerMenu.items && footerMenu.items.length > 0
                ? footerMenu.items
                    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                    .map((group) => {

                      // Handle delivery-info type items
                      if (group.type === "delivery-info") {
                        let deliveryData: any = null;
                        try {
                          if (group.description) {
                            deliveryData = JSON.parse(group.description);
                          }
                        } catch (e) {
                          console.warn("Failed to parse delivery info:", e);
                        }

                        if (!deliveryData) return null;
                        const configuredLocations = (group.children || [])
                          .slice()
                          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                        const hasConfiguredLocations = configuredLocations.length > 0;

                        return (
                          <div
                            key={group.id}
                            className={`${FOOTER_COLUMN_CLASS} inline-flex flex-col items-start gap-4`}
                            style={{ width: "max-content" }}
                          >
                            <h3 className="text-xl font-medium text-text-primary whitespace-nowrap">
                              {group.label}
                            </h3>
                            <div className="inline-flex flex-col items-start gap-3">
                              {deliveryData.showBadge && (
                                <div className="inline-flex items-center justify-center gap-1.5 px-2 py-1 bg-yellow-50 rounded-[6px] text-xs font-medium text-text-primary">
                                  <FlameFilledIcon size={12} className="w-3 h-3" />
                                  {deliveryData.badgeText}
                                </div>
                              )}
                              <div className="text-base font-medium text-text-secondary whitespace-nowrap">
                                Open: {deliveryData.hours}
                              </div>
                              <div className="text-base font-medium text-text-secondary whitespace-nowrap">
                                Min order: ${deliveryData.minOrder}
                              </div>
                              <div className="text-base font-medium text-text-secondary whitespace-nowrap">
                                Delivery fee: $
                                {deliveryData.deliveryFee.toFixed(2)}
                              </div>
                              <div className="text-base font-medium text-text-secondary whitespace-nowrap">
                                Free delivery: $
                                {deliveryData.freeDeliveryThreshold}+
                              </div>
                              <div className="w-full h-px bg-neutral-10" />
                              <div className="w-full">
                                {/* TODO(button-migration): accordion with text + chevron in circle. */}
                                <Button raw
                                  type="button"
                                  onClick={() =>
                                    setExpandedDeliveryInfo(
                                      expandedDeliveryInfo === group.id
                                        ? null
                                        : group.id
                                    )
                                  }
                                  className="flex items-center gap-2 text-sm font-medium text-text-secondary transition-colors"
                                >
                                  <span className="whitespace-nowrap">Select location</span>
                                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-neutral-10">
                                    <ChevronDownIcon
                                      size={12}
                                      className={`text-text-secondary transition-transform ${
                                        expandedDeliveryInfo === group.id ? "rotate-180" : ""
                                      }`}
                                    />
                                  </div>
                                </Button>
                                {expandedDeliveryInfo === group.id && (
                                  <div className="mt-3 space-y-2">
                                    {hasConfiguredLocations ? (
                                      configuredLocations.map((item) => (
                                        <Link
                                          key={item.id}
                                          href={getMenuItemUrl(item)}
                                          className="block text-sm font-medium text-text-secondary transition-colors py-1"
                                        >
                                          {item.type === "collection" && item.collection
                                            ? item.collection.title
                                            : item.label}
                                        </Link>
                                      ))
                                    ) : (
                                      <div className="text-sm text-text-secondary py-1">
                                        No locations configured.
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Handle regular group items
                      return (
                        <div
                          key={group.id}
                          className={`${FOOTER_COLUMN_CLASS} inline-flex flex-col items-start gap-4`}
                          style={{ width: "max-content" }}
                        >
                          <h3 className="text-xl font-medium text-text-primary whitespace-nowrap">
                            {group.label}
                          </h3>
                          {group.children && group.children.length > 0 && (
                            <div className="inline-flex flex-col items-start gap-3">
                              {group.children
                                .slice()
                                .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                                .map((item) => (
                                <Link
                                  key={item.id}
                                  href={getMenuItemUrl(item)}
                                  className="text-base font-medium text-text-secondary transition-colors whitespace-nowrap flex items-center gap-2"
                                >
                                  <span>
                                    {item.type === "collection" &&
                                    item.collection
                                      ? item.collection.title
                                      : item.label}
                                  </span>
                                  {item.badge && (
                                    <span
                                      className={`text-xs px-1.5 py-0.5 rounded ${
                                        item.badgeColor === "red"
                                          ? "bg-red-100 text-red-700"
                                          : item.badgeColor === "green"
                                          ? "bg-green-100 text-green-700"
                                          : item.badgeColor === "blue"
                                          ? "bg-blue-100 text-blue-700"
                                          : "bg-slate-100 text-slate-700"
                                      }`}
                                    >
                                      {item.badge}
                                    </span>
                                  )}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                : null}
            </div>
          </div>
        </div>

        <div className="w-full h-px bg-neutral-10 mb-6" />

        {/* Newsletter Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full mb-6">
          <h2 className="text-2xl font-bold text-text-secondary mb-4 sm:mb-0 whitespace-nowrap">
            Fresh Dank in your inbox
          </h2>

          <form
            onSubmit={handleNewsletterSubmit}
            className="flex w-full sm:w-[400px] items-center gap-3"
          >
            <input
              type="email"
              value={newsletter.email}
              onChange={(e) => newsletter.setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              disabled={newsletter.loading}
              className="flex-1 rounded-xl border border-neutral-10 bg-constant-white px-4 py-3 text-base font-medium text-text-primary placeholder:text-text-tertiary focus:border-blue-80 focus:outline-none focus:ring-2 focus:ring-blue-80/20 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <Button
              type="submit"
              variant="brand"
              size="lg"
              disabled={newsletter.loading}
              className="rounded-xl"
              startIcon={
                newsletter.loading ? (
                  <svg className="h-4 w-4 animate-spin flex-shrink-0" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : undefined
              }
            >
              {newsletter.loading ? "Sending…" : "Subscribe"}
            </Button>
          </form>
        </div>

        {newsletter.success && (
          <p className="mb-4 text-sm text-green-600">
            Thank you for subscribing!
          </p>
        )}
        {newsletter.error && (
          <p className="mb-4 text-sm text-red-600">{newsletter.error}</p>
        )}

        {/* Bottom Section */}
        <div className="flex w-full items-center justify-between px-0 pt-4 border-t border-neutral-10">
          <p className="text-sm font-medium text-text-secondary whitespace-nowrap max-sm:hidden">
            © {currentYear} Dank Calgary Cannabis Dispensary. All rights
            reserved.
          </p>

          <div className="inline-flex items-center gap-3 overflow-x-auto scrollbar-hide pb-1 max-sm:w-max">
            <Link
              href="/privacy-policy"
              className="text-sm font-medium text-text-secondary transition-colors whitespace-nowrap"
            >
              Privacy Policy
            </Link>
            <div className="w-[2px] h-5 bg-neutral-10 flex-shrink-0" />
            <Link
              href="/refund-policy"
              className="text-sm font-medium text-text-secondary transition-colors whitespace-nowrap"
            >
              Refund Policy
            </Link>
            <div className="w-[2px] h-5 bg-neutral-10 flex-shrink-0" />
            <Link
              href="/terms-of-service"
              className="text-sm font-medium text-text-secondary transition-colors whitespace-nowrap"
            >
              Terms of Service
            </Link>
            <div className="w-[2px] h-5 bg-neutral-10 flex-shrink-0" />
            <Link
              href="/license-agreement"
              className="text-sm font-medium text-text-secondary transition-colors whitespace-nowrap"
            >
              License Agreement
            </Link>
            <div className="w-[2px] h-5 bg-neutral-10 flex-shrink-0" />
            <Link
              href="https://aglc.ca/cannabis/retail-cannabis/cannabis-licensee-search"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-text-secondary transition-colors whitespace-nowrap"
            >
              AGLC Licensee Search
            </Link>
          </div>
        </div>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </footer>
  );
}
